import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Card,
  EmptyState,
  Form,
  FormField,
  PageHeader,
  SelectInput,
  TextInput,
  useCreateEntity,
  useEntityDetail,
  useEntityList,
  useUpdateEntity,
} from "@pericles/framework-frontend";

import { generateProjectContext, type ProjectContextSource } from "../api/projectContextClient";
import type { ProjectDetail } from "../api/projectsClient";

interface ProjectFormValues {
  id?: string | number | null;
  name?: string;
  code?: string;
  customerId?: string | number | null;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
  context?: string | null;
  contextSources?: ProjectContextSource[];
  contextGeneratedAt?: string | null;
  contextGeneratedBy?: string | null;
  contextGenerationStatus?: string;
  contextGenerationError?: string | null;
}

interface ProjectFormPanelProps {
  mode: "create" | "edit";
  projectId?: string | number | null;
  activeCustomerId?: string;
  onSaved?: () => void;
  onBack?: () => void;
}

const statusOptions = [
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const textareaClasses = [
  "block w-full rounded-md border border-slate-300 px-3 py-2 text-sm",
  "shadow-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500",
  "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400",
].join(" ");

function formatDateInput(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().split("T")[0];
}

export function ProjectFormPanel({ mode, projectId, activeCustomerId, onSaved, onBack }: ProjectFormPanelProps): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useEntityDetail<ProjectDetail>({
    entity: "Project",
    id: (projectId ?? "") as string,
    enabled: mode === "edit",
  });
  const customersQuery = useEntityList<{ id: string | number; name: string }>({
    entity: "Customer",
    pageSize: 200,
  });
  const createMutation = useCreateEntity<ProjectFormValues, any>({ entity: "Project" });
  const updateMutation = useUpdateEntity<ProjectFormValues, any>({ entity: "Project", id: (projectId ?? "") as string });

  const [formValues, setFormValues] = useState<ProjectFormValues>({
    name: "",
    code: "",
    customerId: activeCustomerId && activeCustomerId !== "all" ? activeCustomerId : undefined,
    status: statusOptions[0].value,
    startDate: null,
    endDate: null,
    context: "",
    contextSources: [],
    contextGenerationStatus: "idle",
    contextGenerationError: null,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(false);

  useEffect(() => {
    if (mode === "edit" && data) {
      setFormValues({
        id: data.id,
        name: data.name,
        code: data.code,
        customerId: data.customerId ?? (typeof data.customer === "object" ? (data.customer as any)?.id : undefined),
        status: data.status,
        startDate: formatDateInput(data.startDate),
        endDate: formatDateInput(data.endDate),
        context: data.context ?? "",
        contextSources: (data.contextSources as ProjectContextSource[] | undefined) ?? [],
        contextGeneratedAt: data.contextGeneratedAt,
        contextGeneratedBy: data.contextGeneratedBy,
        contextGenerationStatus: data.contextGenerationStatus ?? "idle",
        contextGenerationError: data.contextGenerationError ?? null,
      });
    }
  }, [data, mode]);

  const isSubmitting = mode === "create" ? createMutation.isSubmitting : updateMutation.isSubmitting;
  const submitError = mode === "create" ? createMutation.error : updateMutation.error;

  const handleFieldChange = (field: keyof ProjectFormValues, value: unknown) => {
    setFormValues((previous) => ({ ...previous, [field]: value }));
  };

  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenerationMessage(null);

    if (mode === "create") {
      const result = await createMutation.create(formValues);
      if (result) {
        onSaved?.();
        setFormValues({
          name: "",
          code: "",
          customerId: activeCustomerId && activeCustomerId !== "all" ? activeCustomerId : undefined,
          status: statusOptions[0].value,
          startDate: null,
          endDate: null,
          context: "",
          contextGenerationStatus: "idle",
          contextGenerationError: null,
          contextSources: [],
        });
      }
      return;
    }

    const result = await updateMutation.update(formValues);
    if (result) {
      onSaved?.();
      void refetch();
    }
  };

  const handleGenerateContext = async () => {
    if (!projectId) return;
    setIsGenerating(true);
    setGenerationMessage(null);
    setFormValues((previous) => ({ ...previous, contextGenerationStatus: "running", contextGenerationError: null }));

    try {
      const response = await generateProjectContext(String(projectId), { force: true });
      setFormValues((previous) => ({
        ...previous,
        context: response.context ?? previous.context,
        contextSources: response.sources ?? [],
        contextGeneratedAt: response.generatedAt ?? new Date().toISOString(),
        contextGeneratedBy: response.model,
        contextGenerationStatus: "done",
        contextGenerationError: null,
      }));
      setGenerationMessage("Context generated. Review, adjust if needed, then save.");
      await refetch();
    } catch (error: any) {
      const message = error instanceof Error ? error.message : "Failed to generate context";
      setFormValues((previous) => ({ ...previous, contextGenerationStatus: "failed", contextGenerationError: message }));
      setGenerationMessage(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const statusLabel = (formValues.contextGenerationStatus ?? "idle").toString();
  const lastGenerated = formValues.contextGeneratedAt
    ? new Date(formValues.contextGeneratedAt).toLocaleString()
    : "Not generated";

  const customers = useMemo(() => customersQuery.data ?? [], [customersQuery.data]);

  if (mode === "edit" && !projectId) {
    return (
      <Card>
        <span className="text-sm text-red-600">Select a project to edit.</span>
      </Card>
    );
  }

  if (mode === "edit" && isLoading) {
    return (
      <Card>
        <span className="text-sm text-slate-600">Loading…</span>
      </Card>
    );
  }

  if (mode === "edit" && isError) {
    return (
      <Card>
        <span className="text-sm text-red-600">{error instanceof Error ? error.message : "Failed to load project"}</span>
      </Card>
    );
  }

  if (mode === "edit" && !data) {
    return <EmptyState title="Project not found" description="The requested project could not be found." />;
  }

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={mode === "create" ? "Create project" : "Edit project"} onBack={onBack} />

      <Card>
        <Form onSubmit={handleSave} className="flex flex-col gap-4">
          <FormField label="Name" required>
            <TextInput
              required
              value={formValues.name ?? ""}
              onChange={(event) => handleFieldChange("name", event.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Code" required>
            <TextInput
              required
              value={formValues.code ?? ""}
              onChange={(event) => handleFieldChange("code", event.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
          <FormField label="Customer" required>
            <SelectInput
              required
              value={formValues.customerId ?? ""}
              onChange={(event) => handleFieldChange("customerId", event.target.value)}
              disabled={isSubmitting}
            >
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={String(customer.id)}>
                  {customer.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Status">
            <SelectInput
              value={formValues.status ?? statusOptions[0].value}
              onChange={(event) => handleFieldChange("status", event.target.value)}
              disabled={isSubmitting}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Start date">
              <input
                type="date"
                className={textareaClasses}
                value={formValues.startDate ?? ""}
                onChange={(event) => handleFieldChange("startDate", event.target.value || null)}
                disabled={isSubmitting}
              />
            </FormField>
            <FormField label="End date">
              <input
                type="date"
                className={textareaClasses}
                value={formValues.endDate ?? ""}
                onChange={(event) => handleFieldChange("endDate", event.target.value || null)}
                disabled={isSubmitting}
              />
            </FormField>
          </div>

          <FormField label="Project context" description="Freeform notes to capture objectives, scope, risks.">
            <textarea
              className={textareaClasses}
              rows={6}
              value={formValues.context ?? ""}
              onChange={(event) => handleFieldChange("context", event.target.value)}
              disabled={isSubmitting}
            />
          </FormField>

          <div className="rounded border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">Context status: {statusLabel}</span>
                <span className="text-xs text-slate-500">Last generated: {lastGenerated}</span>
                {formValues.contextGenerationError ? (
                  <span className="text-xs text-red-600">{formValues.contextGenerationError}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="button button-secondary button-sm"
                disabled={isGenerating || mode === "create"}
                onClick={handleGenerateContext}
                title={mode === "create" ? "Save the project before generating context" : "Generate project context"}
              >
                {isGenerating ? "Generating..." : "Generate project context (AI)"}
              </button>
            </div>
            {generationMessage ? <p className="mt-2 text-sm text-slate-700">{generationMessage}</p> : null}
            <div className="mt-3">
              <button type="button" className="text-sm text-slate-700 underline" onClick={() => setShowContext((value) => !value)}>
                {showContext ? "Hide" : "Show"} current context summary
              </button>
              {showContext ? (
                <div className="mt-2 rounded bg-slate-50 p-3 text-sm text-slate-800">
                  {formValues.context?.trim() ? formValues.context : "No context captured yet."}
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" className="button button-primary" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : mode === "create" ? "Create project" : "Save changes"}
            </button>
            {submitError ? (
              <span className="text-sm text-red-600">
                {submitError instanceof Error ? submitError.message : String(submitError)}
              </span>
            ) : null}
          </div>

        </Form>
      </Card>
    </div>
  );
}
